/** C1 room check (screens C1): `/check`, no sign-in. Five probes and a screenshot-ready verdict. */
import { useCallback, useEffect, useState } from "react";
import { GxLoading } from "../theme/GxLoading";
import { GxBadge, GxCard, type Tone } from "../theme/primitives";
import { GxTopBar } from "../shell/TopBar";
import { checkCookies, checkFonts, checkStream, checkViewport, checkWorker, type CheckId, type CheckResult, type CheckStatus, type EventSourceLike, type WorkerLike } from "./checks";
import "../shell/shell.css";
import "./check.css";

/** The probes' browser surface; a test swaps any of them to fail that check. */
export interface CheckEnv {
  readonly viewportWidth: () => number;
  readonly doc: { cookie: string };
  readonly secure: boolean;
  readonly openStream: (url: string) => EventSourceLike;
  readonly spawnWorker: () => WorkerLike;
  readonly fonts: { load(font: string): Promise<unknown[]> };
  readonly now: () => number;
}

const browserEnv = (): CheckEnv => ({
  viewportWidth: () => window.innerWidth,
  doc: document,
  secure: window.location.protocol === "https:",
  openStream: (url) => new EventSource(url) as unknown as EventSourceLike,
  spawnWorker: () => new Worker(new URL("./sample.worker.ts", import.meta.url), { type: "module" }),
  fonts: document.fonts as unknown as CheckEnv["fonts"],
  now: () => performance.now(),
});

const ROWS: readonly { readonly id: CheckId; readonly label: string }[] = [
  { id: "viewport", label: "Screen width" },
  { id: "cookies", label: "Cookies" },
  { id: "stream", label: "Live updates" },
  { id: "worker", label: "Forecast worker" },
  { id: "fonts", label: "Fonts" },
];

const ALLOWLIST_HREF = "/it-pack";

const BADGE: Readonly<Record<CheckStatus, { readonly tone: Tone; readonly text: string }>> = {
  pending: { tone: "neutral", text: "Checking" }, pass: { tone: "good", text: "✓ Pass" }, warn: { tone: "warn", text: "! Warn" }, fail: { tone: "crit", text: "✕ Fail" },
};

type Results = Partial<Record<CheckId, CheckResult>>;

/** The four probes that run once; the viewport is live so resizing the window updates its row. */
function useProbes(env: CheckEnv): Results {
  const [results, setResults] = useState<Results>({});
  useEffect(() => {
    let live = true;
    const record = (id: CheckId) => (result: CheckResult) => { if (live) setResults((prev) => ({ ...prev, [id]: result })); };
    record("cookies")(checkCookies(env.doc, env.secure));
    void checkStream(env.openStream, env.now).then(record("stream"));
    void checkWorker(env.spawnWorker, env.now).then(record("worker"));
    void checkFonts(env.fonts).then(record("fonts"));
    return () => { live = false; };
  }, [env]);
  return results;
}

function useViewport(env: CheckEnv): CheckResult {
  const [width, setWidth] = useState(env.viewportWidth());
  const onResize = useCallback(() => setWidth(env.viewportWidth()), [env]);
  useEffect(() => { window.addEventListener("resize", onResize); return () => window.removeEventListener("resize", onResize); }, [onResize]);
  return checkViewport(width);
}

/** What this screen is, by its width: the verdict names the device the person is holding. */
const deviceOf = (width: number): string => (width < 700 ? "phone" : width < 1024 ? "tablet" : "laptop");

function verdict(all: readonly (CheckResult | undefined)[], device: string): { readonly tone: Tone; readonly title: string; readonly failed: number } | null {
  if (all.some((r) => r === undefined)) return null;
  const failed = all.filter((r) => r?.status === "fail").length;
  const warned = all.filter((r) => r?.status === "warn").length;
  if (failed > 0) return { tone: "crit", failed, title: `${failed} check${failed === 1 ? "" : "s"} failed. Send this screen to your IT contact.` };
  if (warned > 0) return { tone: "warn", failed, title: `${warned} check${warned === 1 ? " needs" : "s need"} attention. Send this screen to your IT contact.` };
  return { tone: "good", failed, title: `All clear. This ${device} is ready.` };
}

export function CheckPage({ env }: { readonly env?: CheckEnv } = {}) {
  const [active] = useState(() => env ?? browserEnv());
  const probes = useProbes(active);
  const viewport = useViewport(active);
  const results: Results = { ...probes, viewport };
  const device = deviceOf(active.viewportWidth());
  const done = verdict(ROWS.map((r) => results[r.id]), device);

  return (
    <div className="gx-roomcheck" data-testid="check.page">
      <GxTopBar bankName="Room check" subline="Before the day" role={null} name="" clock={null} />
      <main className="gx-rc-main">
        <GxCard className={`gx-rc-verdict gx-rc-verdict--${done?.tone ?? "pending"}`} testId="check.verdict">
          {done === null
            ? <><h1 className="gx-h1">Checking this {device}…</h1><GxLoading variant="inline" words="This takes a few seconds…" /></>
            : <><h1 className="gx-h1">{done.title}</h1>{done.tone === "good" ? null : <a className="gx-rc-link" href={ALLOWLIST_HREF}>Network allowlist for your IT contact</a>}</>}
        </GxCard>
        <GxCard title="What was checked" testId="check.rows">
          <ul className="gx-rc-rows">
            {ROWS.map(({ id, label }) => {
              const r = results[id];
              const badge = BADGE[r?.status ?? "pending"];
              return (
                <li key={id} className="gx-rc-row" data-testid={`check.row.${id}`} data-status={r?.status ?? "pending"}>
                  <span className="gx-rc-label">{label}</span>
                  <span className="gx-rc-detail">{r?.detail ?? "Running…"}</span>
                  <GxBadge tone={badge.tone}>{badge.text}</GxBadge>
                </li>
              );
            })}
          </ul>
          <p className="gx-rc-meta">{new Date().toISOString().slice(0, 16).replace("T", " ")} UTC · {window.location.host}</p>
          <details className="gx-rc-tech"><summary>Technical details for your IT contact</summary><p className="gx-rc-meta" data-testid="check.useragent">{navigator.userAgent}</p></details>
        </GxCard>
      </main>
    </div>
  );
}
